import { signIn, useSession } from 'next-auth/react';
import { useUserContext } from '../contexts/UserContext';
import { userLogout } from '../helpers/Logout';
import { useRouter } from 'next/router';
import { fireToast } from '../helpers/Toasts';
import { randomStringGenerator } from '../helpers/randomStringGenerator';
import { generateDDRecommendedUserCookie } from '../utils/ddRecommendedUserCookie';
import { User } from '../types';
import { useEffect, useRef } from 'react';
import { AUTHENTICATION_STATUSES } from '../components/PageWrappers/AdDetails/types';
import { ENVIRONMENT_NAME } from '../utils';
import { keycloakLogout } from '../utils/KeycloakLogout';

const useAuthOptions = () => {
  const {
    /**
     * Legacy authentication relied on cookies to store user data and authenticate requests.
     *
     * This cookie is still required for those pages that do not use Keycloak for user information
     * that is not being derived from the session.
     *
     * Once all of data is derived from the session rather than the cookie, references to this cookie
     * may be removed from this hook.
     */
    cookieBasedUser,
    dispatchCookieBasedUser,
    consentOptions: { functionalConsent },
  } = useUserContext();
  const { data: session, status } = useSession();
  const router = useRouter();
  const asPath = router.asPath;
  const cookieBasedUsername = cookieBasedUser?.name || '';
  const isLoggedInKeycloakUser = !!session?.user;
  const shouldReturnAccessToken = !!session?.user && !session?.error;
  const logoutInProgress = useRef(false);

  const handleLogin = (url?: string) => {
    signIn('keycloak', {
      callbackUrl:
        typeof url === 'string'
          ? `/signin?callbackUrl=${url}`
          : `/signin?callbackUrl=${asPath}`,
    });
  };

  const handleLogout = async (callbackUrl?: string, user?: User) => {
    try {
      await keycloakLogout(callbackUrl);
    } catch (error) {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
      return;
    }
    if (functionalConsent) {
      document.cookie = generateDDRecommendedUserCookie(
        randomStringGenerator(),
      );
    }

    /**
     * This cookie is the only method of authentication on C2B pages for users with dealer accounts.
     *
     * However, if one of these users visits a marketplace page, we log them out immediately to avoid using state derived
     * from this cookie. Cookies with dealer user data are for C2B pages only and never marketplace pages
     */
    if (!user || user?.type !== 'DEALER') {
      await userLogout(dispatchCookieBasedUser, cookieBasedUsername);
    }
  };

  /**
   * The duration of the session is longer than the duration of the cookie.
   *
   * This cookie contains user information. We need it to have all user data (including name, vefification status, and registration date)
   * available for a session.
   *
   * If the cookie is not available we should end the session, so that they user may sign in again
   * and have a new cookie set with the requisite user data.
   */
  useEffect(() => {
    if (
      ENVIRONMENT_NAME === 'production' &&
      status === AUTHENTICATION_STATUSES.AUTHENTICATED &&
      !cookieBasedUser &&
      !logoutInProgress.current
    ) {
      logoutInProgress.current = true;
      keycloakLogout().finally(() => {
        logoutInProgress.current = false;
      });
    }
  }, [status, cookieBasedUser]);

  return {
    handleLogin,
    handleLogout,
    isAuthenticated: isLoggedInKeycloakUser,
    accessToken: shouldReturnAccessToken ? session?.accessToken : undefined,
    status,
    user: session?.user,
  };
};

export { useAuthOptions };
